import React, { useState, useEffect } from "react";
import { connect } from "react-redux";

import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonButton,
  IonTitle,
  IonContent,
  IonItem,
  IonLabel,
  IonInput,
  IonNote,
  IonFooter,
  useIonAlert,
} from "@ionic/react";
import Spacer from "components/spacer";

import { WSemit } from "services/socket";
import { removeTeam, updateTeam } from "services/teams";

interface props {
  teams: any;
  selected: number | null;
  onClose: Function;
  removeTeam: Function;
  updateTeam: Function;
  quizId: any;
  customerId: any;
}

interface validation {
  [key: string]: boolean | undefined;
}

const EditTeamModal: React.FC<props> = ({
  teams,
  selected,
  onClose,
  removeTeam,
  updateTeam,
  quizId,
  customerId,
}) => {
  const defaultState = {
    teamName: "",
    playerCount: 2,
    teamEmail: "",
    teamId: "",
  };

  const [presentAlert] = useIonAlert();
  const [team, setTeam] = useState(defaultState);
  const [isTouched, setIsTouched] = useState<validation>({});
  const [isValid, setIsValid] = useState<validation>({});

  const onWillDismiss = (ev: any) => {
    onClose();
  };

  const validateEmail = (email: string) => {
    // Trim whitespace and convert to lowercase for validation
    const trimmedEmail = email.trim().toLowerCase();

    // Check for basic format requirements
    if (!trimmedEmail || trimmedEmail.length === 0) {
      return false;
    }

    // Check for single @ symbol
    const atCount = (trimmedEmail.match(/@/g) || []).length;
    if (atCount !== 1) {
      return false;
    }

    // Split into local and domain parts
    const [localPart, domainPart] = trimmedEmail.split("@");

    // Validate local part (before @)
    if (!localPart || localPart.length === 0 || localPart.length > 64) {
      return false;
    }

    // Validate domain part (after @)
    if (!domainPart || domainPart.length === 0 || domainPart.length > 253) {
      return false;
    }

    // Check for consecutive dots
    if (trimmedEmail.includes("..")) {
      return false;
    }

    // Check for dots at start or end
    if (trimmedEmail.startsWith(".") || trimmedEmail.endsWith(".")) {
      return false;
    }

    // Check for @ at start or end
    if (trimmedEmail.startsWith("@") || trimmedEmail.endsWith("@")) {
      return false;
    }

    // Comprehensive regex validation
    const emailRegex =
      /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

    // Test against regex
    const regexMatch = emailRegex.test(trimmedEmail);

    // Additional domain validation - must contain at least one dot and valid TLD
    const domainParts = domainPart.split(".");
    const hasValidTLD =
      domainParts.length >= 2 &&
      domainParts[domainParts.length - 1].length >= 2;

    return regexMatch && hasValidTLD;
  };

  const validateInput = (field: string, value: string) => {
    setIsValid({ ...isValid, [field]: undefined });
    let validity = false;

    if (field === "email" || field === "teamEmail") {
      // If email is empty, it's valid (optional field)
      if (value.trim() === "") {
        setIsValid({ ...isValid, teamEmail: undefined });
        validity = true;
      } else {
        // Use the boolean return value from validateEmail
        const isEmailValid = validateEmail(value as string);
        if (isEmailValid) {
          setIsValid({ ...isValid, teamEmail: true });
          validity = true;
        } else {
          setIsValid({ ...isValid, teamEmail: false });
        }
      }
    } else if (field === "playerCount") {
      if (!(parseInt(value) > 0)) {
        setIsValid({ ...isValid, playerCount: false });
      } else {
        setIsValid({ ...isValid, playerCount: true });
        validity = true;
      }
    } else if (field === "teamName") {
      if (value === "" || value.trim().length === 0) {
        setIsValid({ ...isValid, teamName: false });
      } else {
        validity = true;
        setIsValid({ ...isValid, teamName: true });
      }
    }
    return validity;
  };

  const markTouched = (e: any) => {
    let field = e.target.name;
    setIsTouched({ ...isTouched, [field]: true });
  };

  const onChange = (e: any) => {
    let field = e.target.name;
    let value = e.target.value;

    if (field === "playerCount") value = parseInt(value);

    validateInput(field, value);
    setTeam({ ...team, [field]: value });
  };

  const checkTeamNameUnique = (teamName: string) => {
    let unique = 0;
    teams?.allTeams.forEach((team: any) => {
      if (team.teamName === teamName) unique++;
    });
    return unique;
  };

  const checkTeamEmailUnique = (teamEmail: string) => {
    let unique = 0;
    teams?.allTeams.forEach((team: any) => {
      if (team.teamEmail === teamEmail) unique++;
    });
    return unique;
  };

  const onSave = () => {
    const teamData = teams.teams.find((t: any) => t.teamId === selected);

    // Validate all required fields before proceeding
    const isPlayerCountValid = validateInput(
      "playerCount",
      team.playerCount.toString()
    );
    const isTeamNameValid = validateInput("teamName", team.teamName);
    const isEmailValid =
      team.teamEmail.trim() === ""
        ? true
        : validateInput("teamEmail", team.teamEmail);

    // Check if team name is provided
    if (!isTeamNameValid) {
      presentAlert({
        header: "Validation Error",
        subHeader: "Team name required!",
        message: "Please enter a valid team name.",
        buttons: [
          {
            text: "OK",
            role: "cancel",
          },
        ],
      });
      return;
    }

    // Check if player count is valid
    if (!isPlayerCountValid) {
      presentAlert({
        header: "Validation Error",
        subHeader: "Invalid player count!",
        message: "Please enter a valid player count (greater than 0).",
        buttons: [
          {
            text: "OK",
            role: "cancel",
          },
        ],
      });
      return;
    }

    // If email is provided, it must be valid
    if (team.teamEmail.trim() !== "" && !isEmailValid) {
      presentAlert({
        header: "Validation Error",
        subHeader: "Invalid email address!",
        message: "Please enter a valid email address or leave the field empty.",
        buttons: [
          {
            text: "OK",
            role: "cancel",
          },
        ],
      });
      return;
    }
    if (teamData.teamEmail !== team.teamEmail) {
      if (
        checkTeamNameUnique(team.teamName) > 0 &&
        checkTeamEmailUnique(team.teamEmail) > 0
      ) {
        presentAlert({
          header: "Alert",
          subHeader: "A team with the same name and email already exists!",
          message: "Please input another name or email.",
          buttons: [
            {
              text: "OK",
              role: "cancel",
              handler: () => {
                // handleRegister();
              },
            },
          ],
        });
      } else {
        presentAlert({
          header: "Alert",
          subHeader: "Please confirm action",
          message:
            "You are about to change the email address for this team. Are you sure you want to continue?",
          buttons: [
            {
              text: "Cancel",
              role: "cancel",
              handler: () => {
                setTeam({ ...team, teamEmail: teamData.teamEmail });
              },
            },
            {
              text: "OK",
              role: "confirm",
              handler: () => {
                updateTeam(team);
                WSemit("updateTeam", {
                  teamId: selected,
                  type: "team",
                  teamEmail: team.teamEmail,
                  teamName: team.teamName,
                  playerCount: team.playerCount,
                  quizId,
                  customerId,
                });
                onWillDismiss({});
              },
            },
          ],
        });
      }
    } else {
      updateTeam(team);
      WSemit("updateTeam", {
        teamId: selected,
        type: "team",
        teamEmail: team.teamEmail,
        teamName: team.teamName,
        playerCount: team.playerCount,
        quizId,
        customerId,
      });
      onWillDismiss({});
    }
  };

  const onRemove = () => {
    presentAlert({
      header: "Alert",
      subHeader: "Please confirm action",
      message:
        "You are about to remove this team. Are you sure you want to continue?",
      buttons: [
        {
          text: "Cancel",
          role: "cancel",
        },
        {
          text: "OK",
          role: "confirm",
          handler: () => {
            removeTeam(selected);
            WSemit("removeTeam", { quizId, customerId, teamId: selected });
            onWillDismiss({});
          },
        },
      ],
    });
  };

  useEffect(() => {
    if (selected) {
      const team = teams.teams.find((t: any) => t.teamId === selected);
      setTeam(team);
    } else {
      setTeam(defaultState);
    }
    //eslint-disable-next-line
  }, [selected]);

  return (
    <IonModal
      isOpen={Boolean(selected)}
      // trigger="open-select-modal"
      onWillDismiss={onWillDismiss}
      style={{ padding: "20px" }}
    >
      <IonHeader>
        <IonToolbar>
          <IonTitle>Edit {team ? team.teamName : ""}</IonTitle>

          <IonButtons slot="end">
            <IonButton onClick={onWillDismiss} color="light">
              Cancel
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonItem
          className={`${isValid.name && "ion-valid"} ${
            isValid.name === false && "ion-invalid"
          } ${isTouched.name && "ion-touched"}`}
        >
          <IonLabel position="floating">Team ID</IonLabel>
          <IonInput disabled={true} value={team.teamId} />
        </IonItem>
        <Spacer size="15" />
        <IonItem
          className={`${isValid.teamName && "ion-valid"} ${
            isValid.teamName === false && "ion-invalid"
          } ${isTouched.teamName && "ion-touched"}`}
        >
          <IonLabel position="floating">Team Name</IonLabel>
          <IonInput
            type="text"
            name="teamName"
            placeholder="Team Name"
            value={team.teamName}
            onIonChange={onChange}
            onIonBlur={markTouched}
          />
          <IonNote slot="helper">Enter a team name</IonNote>
          <IonNote slot="error">Invalid team name</IonNote>
        </IonItem>
        <Spacer size="15" />
        <IonItem
          className={`${isValid.teamEmail && "ion-valid"} ${
            isValid.teamEmail === false && "ion-invalid"
          } ${isTouched.teamEmail && "ion-touched"}`}
        >
          <IonLabel position="floating">Team Email</IonLabel>
          <IonInput
            type="email"
            name="teamEmail"
            placeholder="Team Email"
            value={team.teamEmail}
            onIonChange={onChange}
            onIonBlur={markTouched}
          />
          <IonNote slot="helper">Enter a valid email (optional)</IonNote>
          <IonNote slot="error">Invalid email format</IonNote>
        </IonItem>
        <Spacer size="15" />
        <IonItem
          className={`${isValid.playerCount && "ion-valid"} ${
            isValid.playerCount === false && "ion-invalid"
          } ${isTouched.playerCount && "ion-touched"}`}
        >
          <IonLabel position="floating">Player Count</IonLabel>
          <IonInput
            type="number"
            name="playerCount"
            placeholder="Player Count"
            value={team.playerCount}
            onIonChange={onChange}
            onIonBlur={markTouched}
          />
          <IonNote slot="helper">
            Enter the number of players on the team
          </IonNote>
          <IonNote slot="error">Invalid player count</IonNote>
        </IonItem>
        <Spacer size="15" />

        <Spacer size="60" />
      </IonContent>

      <IonFooter>
        <IonItem>
          <IonButtons slot="end">
            <IonButton color="danger" onClick={onRemove}>
              Remove from quiz
            </IonButton>
            <IonButton color="primary" onClick={onSave}>
              Save
            </IonButton>
          </IonButtons>
        </IonItem>
      </IonFooter>
    </IonModal>
  );
};

const mapStateToProps = (state: any) => ({
  teams: state.teams,
});

export default connect(mapStateToProps, { removeTeam, updateTeam })(
  EditTeamModal
);
